import React, { useState, useEffect, useLayoutEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import styles from '../../_css/Project.module.css';
import { Card, KpiCard } from '../UI';
import EditProjectModal from '../Modals/EditProjectModal';
import ActivatePopUp from '../Modals/ActivatePopUp';
import DeletePopUp from '../Modals/DeletePopUp';
import { font } from '../Simulation/constants';
import { Folder, Zap, Building2, Globe, MoreVertical, User, Layers, ChevronLeft, ChevronRight } from 'lucide-react';
import CreateProject from './CreateProject';
import { createProject_, getAllProjects_, activateProject_, deleteProject_, deleteProjectPermanent_ , editProject_, get_project_stats} from '../../requests';
import { notifySuccess, notifyError } from '../../utils/helper';
import { useSelector, useDispatch } from 'react-redux';
import { Navigate, useNavigate } from 'react-router-dom';
import { setContext } from '../../_store/reducers/contextReducer';
import useRole from '../../hooks/getRole';
import { toggleRefresh } from '../../_store/reducers/refreshReducer';
import { deleteAuthToken } from '../../utils/helper';
import { removeUserInformation } from '../../_store/reducers/userReducer';

/** API may return strings or nested objects like { id, name } */
function displayProjectField(value) {
  if (value == null || value === '') return null;
  if (typeof value === 'object' && value !== null) {
    if (value.name != null && value.name !== '') return String(value.name);
    return null;
  }
  return String(value);
}

function displayCreatedBy(createdBy) {
  if (!createdBy || typeof createdBy !== 'object') return null;
  if (createdBy.full_name != null && createdBy.full_name !== '') return String(createdBy.full_name);
  if (createdBy.name != null && createdBy.name !== '') return String(createdBy.name);
  return null;
}

/** Workflow status: 0 and 9 = inactive; any other status = active. */
function projectWorkflowStatus(p) {
  const s = p?.isActive;
  const inactive = s == false;
  return inactive
    ? { label: 'Inactive', badgeClass: 'statusBadgeMuted' }
    : { label: 'Active', badgeClass: 'statusBadgeReady' };
}

function aiSimulationCount(p) {
  const fromCount = p?._count?.ai_simulations;
  if (typeof fromCount === 'number') return fromCount;
  if (typeof p?.simulations === 'number') return p.simulations;
  if (Array.isArray(p?.ai_simulations)) return p.ai_simulations.length;
  return 0;
}

const PER_PAGE = 10;

const defaultPagination = {
  total: 0,
  totalPages: 0,
  currentPage: 1,
  perPage: PER_PAGE,
  hasNextPage: false,
  hasPrevPage: false,
  nextPage: null,
  prevPage: null,
};

const defaultProjectStats = {
  totalProjects: 0,
  totalSimulations: 0,
  totalActiveProjects: 0,
  totalActiveSimulations: 0,
};

const Index = () => {
  const context = useSelector((state) => state.context);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { brand_id, brand_name } = useSelector((state) => state.context);
  const userInfo = useSelector((state) => state.user);
  const role = useRole();
  const [baseProjects, setBaseProjects] = useState([]);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState(() => ({ ...defaultPagination }));
  const [editProject, setEditProject] = useState(null);
  const [openActionsId, setOpenActionsId] = useState(null);
  const [dropdownAnchor, setDropdownAnchor] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null); // project to deactivate
  const [activateTarget, setActivateTarget] = useState(null); // project to activate
  const [deletePermanentTarget, setDeletePermanentTarget] = useState(null); // project to permanently delete
  const [showCreateProjectModal, setShowCreateProjectModal] = useState(false);
  const [projectStats, setProjectStats] = useState(() => ({ ...defaultProjectStats }));

 

  const handleCardClick = (p) => {
    if (!p?.id || !brand_id) return;
    //is status is inactive the return and show a popup to activate the project
    if (!p.isActive) {
      notifyError('Please activate the project to view the simulations');
      return;
    }
    const resolvedBrandName = brand_name || displayProjectField(p.brand) || '';
    dispatch(
      setContext({
        brand_id,
        brand_name: resolvedBrandName,
        project_id: p.id,
        project_name: p.name ?? '',
      })
    );
    dispatch(toggleRefresh());
    //agency id only if user is super admin
    if(role === 'super_admin') {
      navigate(`/simulations?project_id=${p.id}&brand_id=${brand_id}&agency_id=${userInfo?.agencyId}`);
    } else {
      navigate(`/simulations?project_id=${p.id}&brand_id=${brand_id}`);
    }
  };

  const onNewProject = () => {
    // setView('create');
    setShowCreateProjectModal(true);
  };

  const onSaveEditProject = async (updated) => {
    if(!updated?.id) return;

    const payload = {
      name: updated.name,
      country: updated.country,
      simulation_description: updated.description,
    }
    
    try {
      const response = await editProject_(payload, updated.id);
      if (response?.status < 400) {
        dispatch(toggleRefresh());
        notifySuccess(response?.data?.message || 'Project updated successfully');
        await fetchProjects(page);
        await fetchProjectStats();
      }
    } catch (error) {
      notifyError(error?.response?.data?.message || 'Failed to update project');
      await fetchProjects(page);
    }
  };

  const onCloseCreateProjectModal = () => {
    setShowCreateProjectModal(false);
  };
  const onSaveCreateProject = async (newProject) => {

    const payload = {
      name: newProject.projectName,
      brand_id: newProject.brand_id,
      country: newProject.country,
      simulation_description: newProject.description,
    }

    try {
      const response = await createProject_(payload);
      if (response?.status < 400) {
        dispatch(toggleRefresh());
        notifySuccess(response?.data?.message || 'Project created successfully');


        if (page === 1) {
          await fetchProjects(1);
          await fetchProjectStats();
        } else {
          setPage(1);
        }
      }
    } catch (error) {
      notifyError(error?.response?.data?.message || 'Failed to create project');
    }

    setShowCreateProjectModal(false);
  };

  const handleConfirmActivate = async () => {
    if (!activateTarget) return;
    try {
      const response = await activateProject_(activateTarget.id);
      if (response?.status < 400) {
        dispatch(toggleRefresh());
        notifySuccess(response?.data?.message || 'Project activated successfully');
        setActivateTarget(null);
        await fetchProjects(page);
        await fetchProjectStats();
      } else {
        notifyError(response?.data?.message || 'Failed to activate project');
      }
    } catch (error) {
      notifyError(error?.response?.data?.message || 'Failed to activate project');
    }
  };

  const handleConfirmDeactivate = async () => {
    if (!deleteTarget) return;
    try {
      const response = await deleteProject_(deleteTarget.id);
      if (response?.status < 400) {
        dispatch(toggleRefresh());
        notifySuccess(response?.data?.message || 'Project deactivated successfully');
        setDeleteTarget(null);

        if(context.project_id === deleteTarget.id) {
          const payload = {
            brand_id: brand_id,
            brand_name: brand_name,
            project_id: null,
            project_name: null,
            simulation_id: null,
            simulation_name: null,
          }
          dispatch(setContext(payload))
        }

        await fetchProjects(page);
        await fetchProjectStats();
      } else {
        notifyError(response?.data?.message || 'Failed to deactivate project');
      }
    } catch (error) {
      notifyError(error?.response?.data?.message || 'Failed to deactivate project');
    }
  };

  const handleConfirmDeletePermanent = async () => {
    if (!deletePermanentTarget) return;
    try {
      const response = await deleteProjectPermanent_(deletePermanentTarget.id);
      if (response?.status < 400) {
        dispatch(toggleRefresh());
        notifySuccess(response?.data?.message || 'Project permanently deleted');
        setDeletePermanentTarget(null);

        if (context.project_id === deletePermanentTarget.id) {
          const payload = {
            brand_id: brand_id,
            brand_name: brand_name,
            project_id: null,
            project_name: null,
            simulation_id: null,
            simulation_name: null,
          }
          dispatch(setContext(payload))
        }

        await fetchProjects(page);
        await fetchProjectStats();
      } else {
        notifyError(response?.data?.message || 'Failed to delete project');
      }
    } catch (error) {
      notifyError(error?.response?.data?.message || 'Failed to delete project');
    }
  };

  const fetchProjects = useCallback(
    async (page) => {
      if (!brand_id) return;
      try {
        const response = await getAllProjects_({ page, limit: PER_PAGE, brand_id });
        if (response?.status < 400) {
          const pag = response?.data?.pagination;
          const data = response?.data?.data ?? response?.data ?? [];
          setBaseProjects(Array.isArray(data) ? data : []);
          if (pag) {
            setPagination(pag);
          }
        }
      } catch (error) {
        //is status code 401, then redirect to login page
      if (error?.response?.status === 401) {
        deleteAuthToken()
        dispatch(removeUserInformation())
        navigate('/login')
        notifyError('Session expired. Please login again.' || error?.response?.data?.message)
        setBaseProjects([]);
        setPagination({ ...defaultPagination });
      }
      else{
        notifyError(error?.response?.data?.message || 'Failed to fetch projects');
        setBaseProjects([]);
        setPagination({ ...defaultPagination });

      }
      }
    },
    [brand_id]
  );

  const fetchProjectStats = useCallback(async () => {
    if (!brand_id) {
      setProjectStats({ ...defaultProjectStats });
      return;
    }
    try {
      const response = await get_project_stats({ brand_id });
      if (response?.status < 400) {
        const d = response?.data ?? {};
        setProjectStats({
          totalProjects: Number(d.totalProjects) || 0,
          totalSimulations: Number(d.totalSimulations) || 0,
          totalActiveProjects: Number(d.totalActiveProjects) || 0,
          totalActiveSimulations: Number(d.totalActiveSimulations) || 0,
        });
      }
    } catch (error) {
      notifyError(error?.response?.data?.message || 'Failed to fetch project stats');
    }
  }, [brand_id]);

  useLayoutEffect(() => {
    if (brand_id) {
      setPage(1);
    }

  }, [brand_id]);

  useEffect(() => {
    if (!brand_id) {
      setBaseProjects([]);
      setPagination({ ...defaultPagination });
      setProjectStats({ ...defaultProjectStats });
      setPage(1);
      return;
    }

    fetchProjects(page);
    fetchProjectStats();
  }, [brand_id, page, fetchProjects, fetchProjectStats]);

  const goToPage = (nextPage) => {
    if (nextPage < 1 || nextPage > pagination.totalPages) return;
    setPage(nextPage);
  };

  if(showCreateProjectModal) {
    return (
      <CreateProject
        onClose={onCloseCreateProjectModal}
        onSaveAndGo={onSaveCreateProject}
        onBack={onCloseCreateProjectModal}
      />
    )
  }

  if(!brand_id){
  return <Navigate to="/brands" />
  }

  return (
    <div className={styles.page}>
      <div className={styles.listHeader}>
        <div className={styles.listTitleBlock}>
          <h1 className={styles.pageTitle} style={{ fontFamily: font }}>
            Projects
          </h1>
          <p className={styles.listSubtitle} style={{ fontFamily: font }}>
            Create and manage AI simulation projects
          </p>
        </div>
        <button type="button" onClick={onNewProject} className={styles.btnNew} style={{ fontFamily: font }}>
          + New Project
        </button>
      </div>

      <div className={styles.kpiGrid}>
        <KpiCard
          label="Total projects"
          value={projectStats.totalProjects}
          sub={`Across this brand`}
          icon={<Folder size={24} />}
        />
        <KpiCard
          label="Active projects"
          value={projectStats.totalActiveProjects}
          sub="Across this brand"
          icon={<Layers size={24} />}
        />
        <KpiCard
          label="Total simulations"
          value={projectStats.totalSimulations}
          sub={`${projectStats.totalActiveSimulations} active`}
          icon={<Zap size={24} />}
        />
      </div>

      {editProject && (
        <EditProjectModal
          project={editProject}
          onClose={() => setEditProject(null)}
          onSave={onSaveEditProject}
        />
      )}

      {deleteTarget && (
        <DeletePopUp
          title="Deactivate project"
          message={`Are you sure you want to deactivate "${deleteTarget.name}"?`}
          onClose={() => setDeleteTarget(null)}
          onDelete={handleConfirmDeactivate}
        />
      )}

      {activateTarget && (
        <ActivatePopUp
          title="Activate project"
          message={`Are you sure you want to activate "${activateTarget.name}"?`}
          onClose={() => setActivateTarget(null)}
          onActivate={handleConfirmActivate}
        />
      )}

      {deletePermanentTarget && (
        <DeletePopUp
          variant="delete"
          title="Delete project"
          message={`This will permanently delete "${deletePermanentTarget.name}" and all of its simulations. This cannot be undone.`}
          onClose={() => setDeletePermanentTarget(null)}
          onDelete={handleConfirmDeletePermanent}
        />
      )}

      {/* <SearchBar placeholder="Search projects..." value={searchTerm} onChange={setSearchTerm} /> */}

      <div className={styles.cardsList}>
        {baseProjects.length > 0 ? baseProjects.map((p) => {
          const brandLabel = displayProjectField(p.brand);
          const countryLabel = displayProjectField(p.country ?? p.market);
          const industryLabel = displayProjectField(p.industry);
          const creatorLabel = displayCreatedBy(p.created_by);
          const simCount = aiSimulationCount(p);
          const isActive = p.isActive;
          const workflow = projectWorkflowStatus(p);
          const workflowClass = styles[workflow.badgeClass] ?? styles.statusBadgeMuted;
          return (
          <Card key={p.id} style={{ cursor: 'pointer' }} >
            <div className={styles.projectCard}>
              <div className={styles.projectCardMain} onClick={() => handleCardClick(p)}>
                <div className={styles.projectTitleRow}>
                  <div className={styles.projectName} style={{ fontFamily: font }}>
                    {p.name}
                  </div>
                  <span className={`${styles.statusBadge} ${workflowClass}`} style={{ fontFamily: font }}>
                    {workflow.label}
                  </span>
                </div>
                <div className={styles.projectMeta}>
                  {brandLabel && (
                    <span className={styles.metaItem} style={{ fontFamily: font }}>
                      <Building2 size={12} />
                      {brandLabel}
                    </span>
                  )}
                  {countryLabel && (
                    <span className={styles.metaItem} style={{ fontFamily: font }}>
                      <Globe size={12} />
                      {countryLabel}
                    </span>
                  )}
                  {creatorLabel && (
                    <span className={styles.metaItem} style={{ fontFamily: font }}>
                      <User size={12} />
                      {creatorLabel}
                    </span>
                  )}
                  {industryLabel && (
                    <span className={styles.metaItem} style={{ fontFamily: font }}>
                      <Folder size={12} />
                      {industryLabel}
                    </span>
                  )}
                </div>
              </div>
              <div className={styles.cardRight}>
                <div className={styles.simBlock}>
                  <div className={styles.simValue} style={{ fontFamily: font }}>
                    {simCount}
                  </div>
                  <div className={styles.simLabel} style={{ fontFamily: font }}>
                    AI simulations
                  </div>
                </div>
                <div className={styles.dropdownWrap}>
                  <button
                    type="button"
                    className={styles.actionBtn}
                    title="Actions"
                    aria-haspopup="true"
                    aria-expanded={openActionsId === p.id}
                    onClick={(e) => {
                      e.stopPropagation();
                      const rect = e.currentTarget.getBoundingClientRect();
                      if (openActionsId === p.id) {
                        setOpenActionsId(null);
                        setDropdownAnchor(null);
                      } else {
                        setDropdownAnchor({ top: rect.bottom, right: rect.right, left: rect.left });
                        setOpenActionsId(p.id);
                      }
                    }}
                  >
                    <MoreVertical size={18} />
                  </button>
                  {openActionsId === p.id &&
                    dropdownAnchor &&
                    ReactDOM.createPortal(
                      <>
                        <div
                          className={styles.dropdownBackdrop}
                          onClick={(e) => {
                            e.stopPropagation();
                            setOpenActionsId(null);
                            setDropdownAnchor(null);
                          }}
                          aria-hidden="true"
                        />
                        <ul
                          className={styles.dropdownMenu}
                          role="menu"
                          style={{
                            top: dropdownAnchor.top + 4,
                            right: typeof window !== 'undefined' ? window.innerWidth - dropdownAnchor.right : 0,
                          }}
                        >
                          <li role="none">
                            <button
                              type="button"
                              role="menuitem"
                              className={styles.dropdownItem}
                              onClick={() => {
                                setEditProject(p);
                                setOpenActionsId(null);
                                setDropdownAnchor(null);
                              }}
                            >
                              Edit
                            </button>
                          </li>
                          <li role="none">
                            <button
                              type="button"
                              role="menuitem"
                              className={isActive ? styles.dropdownItemDanger : styles.dropdownItem}
                              onClick={() => {
                                if (isActive) {
                                  setDeleteTarget(p);
                                } else {
                                  setActivateTarget(p);
                                }
                                setOpenActionsId(null);
                                setDropdownAnchor(null);
                              }}
                            >
                              {isActive ? 'Deactivate' : 'Activate'}
                            </button>
                          </li>
                          <li role="none">
                            <button
                              type="button"
                              role="menuitem"
                              className={styles.dropdownItemDanger}
                              onClick={() => {
                                setDeletePermanentTarget(p);
                                setOpenActionsId(null);
                                setDropdownAnchor(null);
                              }}
                            >
                              Delete
                            </button>
                          </li>
                        </ul>
                      </>,
                      document.body
                    )}
                </div>
              </div>
            </div>
          </Card>
          );
        }) : (
          <div className={styles.listEmpty}>
            No projects found. Please create a new project.
          </div>
        )}
      </div>

      {brand_id && pagination.totalPages > 0 && (
        <div className={styles.pagination}>
          <span className={styles.paginationInfo}>
            Showing{' '}
            {pagination.total === 0
              ? 0
              : (pagination.currentPage - 1) * pagination.perPage + 1}
            –{Math.min(pagination.currentPage * pagination.perPage, pagination.total)} of {pagination.total}
          </span>
          {pagination.totalPages > 0 && (
            <div className={styles.paginationBtns}>
              <button
                type="button"
                onClick={() => goToPage(pagination.prevPage ?? pagination.currentPage - 1)}
                disabled={!pagination.hasPrevPage}
                className={styles.pageBtn}
                title="Previous page"
              >
                <ChevronLeft size={16} />
              </button>
              <button
                type="button"
                onClick={() => goToPage(pagination.nextPage ?? pagination.currentPage + 1)}
                disabled={!pagination.hasNextPage}
                className={styles.pageBtn}
                title="Next page"
              >
                <ChevronRight size={16} />
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Index;
